/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IRouteListEntry } from "@adapptio/lib-blueprint";
import {
	HTTP_STATUS,
	HttpClient,
	HttpError,
	IAccount,
	IActionParams,
	IApiResponse,
	IBlueprintContentMetadata,
	IBlueprintMetadata,
	IEditorApiConfigResponse,
	IEditorApiDeployEnvironmentListResponse,
	IEditorApiDeploymentListResponse,
	IEditorApiErrorListResponse,
	IEditorApiIconPackResponse,
	IEditorApiReleaseListResponse,
	IEditorApiSessionResponse,
	IEndpointParams,
	IScmInfo,
	IViewApiResponse,
	TActionResult,
	TEditorApiAppMetadataResponse,
	TEditorApiGetIntegrationsResponse,
	TEditorApiResponseCreateBlueprintResponse,
	TEditorApiResponseGetResourcesResponse,
} from "@adapptio/lib-core";
import { createEventEmitter, emitEvent, onEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { IEditor } from "../../IEditor";
import { StateService } from "../../state/StateService";
import { API_ERROR_NAME, ApiError } from "../ApiErrors";
import { IApiLoginResponse } from "../ApiTypes";
import { IEditorApiService } from "../IEditorApiService";

/**
 * Options for an Editor API Service
 */
export interface IEditorRemoteApiServiceOpts {
	apiUrl: string;
}

interface IEditorRemoteApiServiceState {
	sessionId: string;
}

/**
 * Editor API Service
 */
export class EditorRemoteApiService implements IEditorApiService {
	private static STATE_KEY__SESSION = "apiService.session";

	/* Services */
	private stateSvc: StateService;

	/** HTTP Client instance */
	private httpClient: HttpClient;

	/** Editor user object */
	private editorIdentity: IAccount = null;

	/** Emitted when an editor identity was set */
	public onAuthorize = createEventEmitter<void>();

	/** Event emitted when any of the API requests ends up with an unauthorized response */
	public onUnauthorized = createEventEmitter<void>();

	/**
	 * Service constructor
	 *
	 * @param opts Options
	 */
	public constructor(private dc: IEditor, opts: IEditorRemoteApiServiceOpts) {
		this.stateSvc = dc.get("stateService");

		this.stateSvc.register<IEditorRemoteApiServiceState>(EditorRemoteApiService.STATE_KEY__SESSION, {
			sessionId: null
		});

		this.httpClient = new HttpClient(opts.apiUrl);
	}

	public getRealApiUrl(): string {
		return this.httpClient.getServerUrl();
	}

	/**
	 * Service init
	 */
	public async init(): Promise<void> {
		onEvent(this.httpClient.onError, (err) => {
			if (err instanceof HttpError && err.status === HTTP_STATUS.UNAUTHORIZED) {
				if (this.editorIdentity) {
					emitEvent(this.onUnauthorized);
				}
			}
		});
	}

	private async ping(): Promise<void> {
		return (await this.httpClient.get<void>("/api/editor/v1/ping"))?.body;
	}

	/**
	 * Method to dispose the service
	 */
	public async dispose(): Promise<void> {
		removeAllEventListeners(this.httpClient.onError);
		removeAllEventListeners(this.onUnauthorized);
	}

	/**
	 * Sets and stores the session ID
	 *
	 * @param sessionId Session ID
	 */
	private setSessionId(sessionId: string): void {
		this.stateSvc.set<IEditorRemoteApiServiceState>(EditorRemoteApiService.STATE_KEY__SESSION, {
			sessionId: sessionId
		});

		this.httpClient.setHeader("x-session-id", sessionId);
	}

	/**
	 * Returns current session ID
	 */
	public getSessionId(): string {
		return this.stateSvc.get<IEditorRemoteApiServiceState>(EditorRemoteApiService.STATE_KEY__SESSION)
			.sessionId;
	}

	public getEditorIdentity(): IAccount {
		return this.editorIdentity;
	}

	/**
	 * Tries to restore last session and returns whenever it was successful
	 * @returns True when session was restored, false otherwise
	 */
	public async tryRestoreSession(): Promise<boolean> {
		const state = this.stateSvc.get<IEditorRemoteApiServiceState>(
			EditorRemoteApiService.STATE_KEY__SESSION
		);

		if (state.sessionId) {
			this.httpClient.setHeader("x-session-id", state.sessionId);

			try {
				await this.loadEditorSession();
				return true;
			} catch (err) {
				// ignore
				return false;
			}
		} else {
			return false;
		}
	}

	/**
	 * Logs the user in using temporary token and setup session
	 *
	 * @param accessToken Access token
	 */
	public async auth(accessToken: string): Promise<void> {
		try {
			const sessionRes = await this.httpClient.post<IApiResponse<IApiLoginResponse>>(
				`/auth/v1/token/login`,
				null,
				{
					accessToken
				}
			);

			this.setSessionId(sessionRes.body.data.sessionId);
		} catch (err) {
			if (err instanceof HttpError && err.status === HTTP_STATUS.UNAUTHORIZED) {
				throw new ApiError(
					API_ERROR_NAME.AUTH_INVALID_TOKEN,
					"Failed to authenticate: Invalid intermediate token or failed to exchange token.",
					err
				);
			} else if (err instanceof HttpError && err.status === HTTP_STATUS.FORBIDDEN) {
				throw new ApiError(
					API_ERROR_NAME.AUTH_FORBIDDEN,
					"Failed to authenticate: Access denied.",
					err
				);
			} else {
				throw new ApiError(
					API_ERROR_NAME.UNEXPECTED_ERROR,
					"Failed to authenticate session using token - unexpected error.",
					err
				);
			}
		}

		// Load editor session
		await this.loadEditorSession();
	}

	/**
	 * Loads an editor session and account
	 */
	public async loadEditorSession(): Promise<void> {
		// Check if session is valid
		let session: IEditorApiSessionResponse;

		try {
			session = await this.getEditorSession();
		} catch (err) {
			this.editorIdentity = null;

			throw new ApiError(
				API_ERROR_NAME.UNEXPECTED_ERROR,
				"Failed to validate session - unexpected error.",
				err
			);
		}

		if (!session?.editorLoggedIn) {
			this.editorIdentity = null;
			throw new ApiError(API_ERROR_NAME.AUTH_NOT_LOGGED_IN, "Current session is not logged in.");
		}

		// Assign editor identity
		this.editorIdentity = session.identity;
		emitEvent(this.onAuthorize);
	}

	private async waitForServerOnline(timeToFetch = 0): Promise<void> {
		const currentTime = await new Promise<number>((resolve) => requestAnimationFrame(resolve));

		try {
			if (timeToFetch <= currentTime) {
				timeToFetch = currentTime + 500;

				await this.ping();
				return;
			}
		} catch (err) {
			// keep trying
		}

		return this.waitForServerOnline(timeToFetch);
	}

	private async getData<TData>(path: string): Promise<TData> {
		let response = await this.httpClient.get<IApiResponse<TData>>(path);
		const data = Array.isArray(response.body.data) ? [...response.body.data] : { ...response.body.data };
		response.body = null;
		response = null;
		return data as TData;
	}

	/**
	 * Returns application meta-data
	 *
	 * @todo Change return type!
	 */
	public async getAppMeta(): Promise<TEditorApiAppMetadataResponse> {
		return (
			await this.httpClient.get<IApiResponse<TEditorApiAppMetadataResponse>>("/api/editor/v1/app/meta")
		).body.data;
	}

	/**
	 * Returns application config
	 */
	public async getAppConfig(): Promise<IEditorApiConfigResponse> {
		return (
			await this.httpClient.get<IApiResponse<IEditorApiConfigResponse>>(
				"/api/editor/v1/config/resolved"
			)
		).body.data;
	}

	/**
	 * Returns resource list
	 */
	public async getResourceList(): Promise<TEditorApiResponseGetResourcesResponse> {
		return this.getData<TEditorApiResponseGetResourcesResponse>("/api/editor/v1/resource/list");
	}

	/**
	 * Returns blueprint content with meta-data
	 */
	public async getBlueprintContent(path: string): Promise<IBlueprintContentMetadata> {
		return (
			await this.httpClient.get<IApiResponse<IBlueprintContentMetadata>>(
				`/api/editor/v1/resource/blueprint/content/${path}`
			)
		).body.data;
	}

	/**
	 * Creates a new blueprint using content
	 */
	public async createBlueprint(
		basePath: string,
		name: string,
		content: string
	): Promise<TEditorApiResponseCreateBlueprintResponse> {
		return (
			await this.httpClient.post<IApiResponse<TEditorApiResponseCreateBlueprintResponse>>(
				`/api/editor/v1/resource/blueprint`,
				null,
				{
					basePath: basePath,
					name: name,
					content: content
				}
			)
		).body.data;
	}

	/**
	 * Creates a new directory
	 */
	public async createDirectory(path: string): Promise<void> {
		await this.httpClient.post<void>(`/api/editor/v1/resource/dir/${path}`);
	}

	/**
	 * Updates blueprint content
	 */
	public async updateBlueprintContent(
		path: string,
		content: string,
		sourceRevision: string,
		commitMessage: string
	): Promise<IBlueprintMetadata> {
		return (
			await this.httpClient.put<IApiResponse<IBlueprintMetadata>>(
				`/api/editor/v1/resource/blueprint/content/${path}`,
				null,
				{
					content: content,
					sourceRevision: sourceRevision,
					commitMessage: commitMessage
				}
			)
		).body.data;
	}

	/**
	 * Deletes blueprint
	 */
	public async deleteBlueprint(path: string): Promise<void> {
		await this.httpClient.delete<void>(`/api/editor/v1/resource/blueprint/content/${path}`);
	}

	/**
	 * Deletes directory
	 */
	public async deleteDirectory(path: string): Promise<void> {
		await this.httpClient.delete<void>(`/api/editor/v1/resource/dir/${path}`);
	}

	/**
	 * Renames and/or moves blueprint
	 */
	public async renameAndMoveBlueprint({
		path,
		basePath,
		name
	}: {
		path: string;
		basePath: string;
		name: string;
	}): Promise<IBlueprintMetadata> {
		return (
			await this.httpClient.post<IApiResponse<IBlueprintMetadata>>(
				`/api/editor/v1/resource/blueprint/rename/${path}`,
				null,
				{
					basePath,
					name
				}
			)
		).body.data;
	}

	/**
	 * Renames and/or moves directory
	 */
	public async renameAndMoveDirectory({
		path,
		basePath,
		name
	}: {
		path: string;
		basePath: string;
		name: string;
	}): Promise<void> {
		await this.httpClient.put<IApiResponse<undefined>>(`/api/editor/v1/resource/dir/${path}`, null, {
			basePath,
			name
		});
	}

	/**
	 * Returns route list
	 */
	public async getRouteList(): Promise<IRouteListEntry[]> {
		return (await this.httpClient.get<IApiResponse<IRouteListEntry[]>>("/api/editor/v1/routes")).body
			.data;
	}

	/**
	 * Returns a list of registered integration types
	 */
	public async getIntegrationTypes(): Promise<TEditorApiGetIntegrationsResponse> {
		return (
			await this.httpClient.get<IApiResponse<TEditorApiGetIntegrationsResponse>>(
				"/api/editor/v1/integration/list"
			)
		).body.data;
	}

	/**
	 * Returns view by ID
	 *
	 * @param viewId View ID
	 */
	public async getView(viewId: string): Promise<IViewApiResponse> {
		return (
			await this.httpClient.get<IApiResponse<IViewApiResponse>>(
				"/api/editor/v1/view/" + encodeURIComponent(viewId)
			)
		).body.data;
	}

	/**
	 * Invokes an action
	 *
	 * @param actionId Action ID
	 * @param params Action params
	 * @param opts Options
	 */
	public async invokeAction(
		actionId: string,
		params: IActionParams,
		opts: {
			withTypeDescriptor?: boolean;
			debug?: boolean;
		}
	): Promise<TActionResult> {
		return (
			await this.httpClient.post<IApiResponse<TActionResult>>(
				`/api/editor/v1/action/invoke/${encodeURIComponent(actionId)}`,
				opts,
				params
			)
		).body.data;
	}

	/**
	 * Invokes an endpoint
	 */
	public async invokeEndpoint(
		endpointId: string,
		params: IEndpointParams & { [K: string]: string },
		opts: {
			withTypeDescriptor?: boolean;
			debug?: boolean;
		}
	): Promise<TActionResult> {
		return (
			await this.httpClient.post<IApiResponse<TActionResult>>(
				`/api/editor/v1/endpoint/invoke/${encodeURIComponent(endpointId)}`,
				opts,
				params
			)
		).body.data;
	}

	public async autologinAppUser(): Promise<void> {
		await this.httpClient.post<void>(`/api/editor/v1/session/editor/autologin`);
	}

	/**
	 * Returns session
	 */
	public async getSession(reloadUserAccount?: boolean): Promise<IEditorApiSessionResponse> {
		let path = "/api/editor/v1/session";
		if (reloadUserAccount === true) {
			path += "?reloadUserAccount=true";
		}
		return (await this.httpClient.get<IApiResponse<IEditorApiSessionResponse>>(path)).body.data;
	}

	/**
	 * Returns editor's session
	 */
	public async getEditorSession(): Promise<IEditorApiSessionResponse> {
		return (
			await this.httpClient.get<IApiResponse<IEditorApiSessionResponse>>(
				"/api/editor/v1/session/editor"
			)
		).body.data;
	}

	/**
	 * Returns errors
	 */
	public async getErrors(): Promise<IEditorApiErrorListResponse> {
		return this.getData<IEditorApiErrorListResponse>("/api/editor/v1/errors");
	}

	/*
	 * Returns icon packs
	 */
	public async getIconPacks(): Promise<IEditorApiIconPackResponse> {
		return (
			await this.httpClient.get<IApiResponse<IEditorApiIconPackResponse>>(
				"/api/editor/v1/assets/iconPacks"
			)
		).body.data;
	}

	public async deleteIntegrationCache({
		integrationId
	}: {
		integrationId: string;
	}): Promise<void> {
		return (
			await this.httpClient.delete<void>(`/api/editor/v1/integration/cache/${integrationId}`)
		).body;
	}

	/*
	 * Uploads new assets
	 */
	public async addAssets(basePath: string, files: FileList): Promise<IBlueprintMetadata[]> {
		const data = new FormData();

		for (let i = 0; i < files.length; i++) {
			data.append("upload", files[i]);
		}

		return (
			await this.httpClient.post<IApiResponse<IBlueprintMetadata[]>>(
				`/api/editor/v1/assets`,
				{ basePath },
				data
			)
		).body.data;
	}

	/*
	 * Updates asset
	 */
	public async updateAsset(
		assetId: string,
		files: FileList,
		sourceRevision?: string
	): Promise<IBlueprintMetadata> {
		const data = new FormData();

		for (let i = 0; i < files.length; i++) {
			data.append("upload", files[i]);
		}

		return (
			await this.httpClient.put<IApiResponse<IBlueprintMetadata>>(
				`/api/editor/v1/assets/${assetId}`,
				sourceRevision ? { sourceRevision } : null,
				data
			)
		).body.data;
	}

	///*
	// * Deletes asset
	// */
	//public async deleteAsset(assetId: string): Promise<void> {
	//	return (
	//		await this.httpClient.delete<void>(`/api/editor/v1/assets/${assetId}`,null)
	//	).body;
	//}

	public getThemeCssUrl({ themeId, styleName }: { themeId: string; styleName?: string }): string {
		const url = new URL(`/css/v1/theme/${themeId}`, this.getRealApiUrl());

		if (styleName) {
			url.searchParams.set("styleName", encodeURIComponent(styleName));
		}

		return url.href;
	}

	public async getThemeCss({
		themeId,
		styleName
	}: {
		themeId: string;
		styleName?: string;
	}): Promise<string> {
		return (await this.httpClient.get<IApiResponse<string>>(`/css/v1/theme/${themeId}`, { styleName }))
			.body.data;
	}

	/*
	 * Get manifest
	 */
	public async getManifest() {
		return (
			await this.httpClient.get<IApiResponse<IBlueprintContentMetadata>>(
				`/api/editor/v1/resource/manifest/content`
			)
		).body.data;
	}

	/*
	 * Update manifest
	 */
	public async updateManifest(content: string, sourceRevision: string) {
		const result = (
			await this.httpClient.put<IApiResponse<IBlueprintMetadata>>(
				`/api/editor/v1/resource/manifest/content`,
				null,
				{
					content,
					sourceRevision
				}
			)
		).body.data;

		await this.waitForServerOnline();

		return result;
	}

	/**
	 * Returns a list of registered integration types
	 */
	public async getManifestDefinition(): Promise<any> {
		return (await this.httpClient.get<IApiResponse<any>>("/api/editor/v1/manifest/definition")).body.data;
	}

	/**
	 * Returns SCM info
	 */
	public async getScmInfo(): Promise<IScmInfo|null> {
		return this.getData<IScmInfo|null>("/api/editor/v1/scm/info");
	}

	/**
	 * Returns release list
	 */
	public async getReleaseList(): Promise<IEditorApiReleaseListResponse> {
		return this.getData<IEditorApiReleaseListResponse>("/api/editor/v1/scm/releases");
	}

	/**
	 * Returns deployment environment list
	 */
	public async getDeploymentEnvironmentList(): Promise<IEditorApiDeployEnvironmentListResponse> {
		return this.getData<IEditorApiDeployEnvironmentListResponse>(
			"/api/editor/v1/deployment/environments"
		);
	}

	/**
	 * Returns deployment list
	 */
	public async getDeploymentList(): Promise<IEditorApiDeploymentListResponse> {
		return this.getData<IEditorApiDeploymentListResponse>("/api/editor/v1/deployment/deployments");
	}

	/**
	 * Creates a new release
	 *
	 * @param tagName Tag name
	 * @param notes Release notes
	 */
	public async createRelease(tagName: string, releaseNotes: string): Promise<void> {
		await this.httpClient.post<void>("/api/editor/v1/scm/releases", null, {
			tagName,
			releaseNotes
		});
	}

	/**
	 * Deploys a release to an environment
	 *
	 * @param envName Environment name
	 * @param tagName Release tag name
	 */
	public async deploy(envName: string, tagName: string): Promise<void> {
		await this.httpClient.post<void>("/api/editor/v1/deployment/deployments", null, {
			environment: envName,
			tagName
		});
	}

	/**
	 * Reloads server
	 */
	public async reloadServer(): Promise<void> {
		await this.httpClient.post<void>("/api/editor/v1/server/reload");
	}
}
